import axios from 'axios';

// .envから環境変数を取得
const CHOWA_API_KEY = process.env.REACT_APP_CHOWA_API_KEY;
const CHOWA_UNITS_API = process.env.REACT_APP_CHOWA_UNITS_API;
const CHOWA_SERIES_API = process.env.REACT_APP_CHOWA_SERIES_API;

// 共通ヘッダー（APIキーを自動付与）
const getHeaders = () => ({
  'X-API-KEY': CHOWA_API_KEY,
  'Content-Type': 'application/json',
});

// ==============================================
// Chowa API関数群（新しい形式）
// ==============================================

/**
 * 新しい形式の機種・排ガス規制の組み合わせに対応したデータ取得
 * @param {string} seriesKey - シリーズキー（例: 'HV'）
 * @param {string} modelWithExhaust - 機種名+排ガス規制（例: 'HV-300(Ⅲ)'）
 * @returns {Promise} 該当する全ユニットのデータ
 */
export const getEngineDataByModelAndExhaust = async (seriesKey, modelWithExhaust) => {
  try {
    // 機種名と排ガス規制を分離
    // 例: 'HV-300(Ⅲ)' → baseModel: 'HV-300', exhaust: 'Ⅲ'
    const match = modelWithExhaust.match(/^(.+)\((.+)\)$/);
    if (!match) {
      throw new Error('Invalid model format');
    }
    
    const [, baseModel, exhaust] = match;
    
    // 該当するシリーズの全ユニットを取得
    const unitsResponse = await getModelsBySeriesName(seriesKey);
    
    if (unitsResponse.data?.status === 'success' && unitsResponse.data?.data?.units) {
      // 指定された機種・排ガス規制に該当するユニットをフィルタリング
      const filteredUnits = unitsResponse.data.data.units.filter(unit => {
        if (!unit.model_type || !unit.exhaust_gas) return false;
        
        // model_typeから個別識別子を除去して比較
        const unitBaseModel = unit.model_type.replace(/#.*$/, '');
        return unitBaseModel === baseModel && unit.exhaust_gas === exhaust;
      });
      
      // データを既存の形式に変換
      const engineData = filteredUnits.map(unit => {
        // latest_dataから稼働時間を取得
        let hours = 0;
        let isCanError = false;
        let validData = null;
        let latestData = null;
        
        // 新しいロジック: latest_dataとsecond_dataを組み合わせて使用
        let latestTimestamp = null;
        let latestIsError = false;
        let secondHours = 0;
        let secondTimestamp = null;
        
        // latest_dataの解析
        if (unit.latest_data) {
          try {
            const latestData = typeof unit.latest_data === 'string' ? 
              JSON.parse(unit.latest_data) : unit.latest_data;
            
            const latestHours = latestData.operating_hours || 999999;
            latestTimestamp = latestData.timestamp;
            latestIsError = latestHours >= 99999;
            
            console.log(`🔍 ${unit.custom_data} - latest_data:`, {
              timestamp: latestTimestamp,
              hours: latestHours,
              isError: latestIsError
            });
            
            if (!latestIsError) {
              // latest_dataが実際のデータの場合はそのまま使用
              hours = latestHours;
              isCanError = false;
            }
          } catch (e) {
            console.warn('Error parsing latest_data:', e);
          }
        }
        
        // second_dataの解析（latest_dataがエラーの場合に稼働時間で使用）
        if (unit.second_data) {
          try {
            const secondData = typeof unit.second_data === 'string' ? 
              JSON.parse(unit.second_data) : unit.second_data;
            
            secondHours = secondData.operating_hours || 999999;
            secondTimestamp = secondData.timestamp;
            
            // latest_dataがエラーで、second_dataが実際のデータの場合
            if (latestIsError && secondHours < 99999) {
              hours = secondHours;
              // 表示は: 黄色でlatest時刻 + 白でsecond稼働時間
              isCanError = true; // 時刻表示を黄色にするため
            }
          } catch (e) {
            console.warn('Error parsing second_data:', e);
          }
        }
        
        // latest_dataがエラーでsecond_dataもない/エラーの場合
        if (latestIsError && secondHours >= 99999) {
          hours = 0; // 稼働時間は表示しない
          isCanError = true; // 時刻表示を黄色にするため
        }
        
        return {
          deviceId: unit.device || '', // deviceがnullの場合は空文字列を渡し、表示側で'-'に変換
          name: unit.custom_data || 'Unknown', // グラフのラベルにはcustom_dataを使用
          hours: hours,
          isCanError: isCanError,
          validData: validData,
          latestData: latestData,
          operationDays: 0, // 稼働日数は別途計算が必要
          modelSeries: unit.model_series || '',
          modelType: unit.model_type || '',
          unitSerialNumber: unit.unit_serial_number || '',
          customData: unit.custom_data || '',
          timestamp: latestTimestamp,
          // 追加情報: second_dataがある場合の情報
          latestIsError: latestIsError,
          secondHours: secondHours,
          secondTimestamp: secondTimestamp,
          hasValidSecondData: secondHours > 0 && secondHours < 99999,
          exhaustGas: unit.exhaust_gas || '' // 排ガス規制を追加
        };
      });
      
      return {
        engineData: engineData,
        totalUnits: engineData.length,
        operatingUnits: engineData.filter(item => item.hours > 0).length
      };
    }
    
    return {
      engineData: [],
      totalUnits: 0,
      operatingUnits: 0
    };
    
  } catch (error) {
    console.error('Error fetching engine data by model and exhaust:', error);
    throw error;
  }
};

// ==============================================
// UnitManagement API関数群
// ==============================================

/**
 * ChowaUnit一覧取得API
 * @param {Object} filters - フィルタリング条件
 * @param {string} filters.model_series - 機種シリーズ
 * @param {string} filters.exhaust_gas - 排ガス規制
 * @param {string} filters.search - 検索文字列
 * @returns {Promise} APIレスポンス
 */
export const getChowaUnits = (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.model_series) params.append('model_series', filters.model_series);
  if (filters.exhaust_gas) params.append('exhaust_gas', filters.exhaust_gas);
  if (filters.search) params.append('search', filters.search);
  
  const url = filters.model_series || filters.exhaust_gas || filters.search 
    ? `${CHOWA_UNITS_API}?${params.toString()}`
    : CHOWA_UNITS_API;
    
  return axios.get(url, {
    headers: getHeaders(),
  });
};

/**
 * ChowaUnit新規作成API
 * @param {Object} unitData - ユニットデータ
 * @param {string} unitData.custom_data - カスタムデータ
 * @param {number} unitData.model_series_id - 機種シリーズID
 * @param {string} unitData.model_type - 機種号機
 * @param {string} unitData.unit_serial_number - ユニット製造番号
 * @param {string} unitData.exhaust_gas - 排ガス規制
 * @param {string} unitData.device - デバイスID
 * @returns {Promise} APIレスポンス
 */
export const createChowaUnit = (unitData) => {
  return axios.post(CHOWA_UNITS_API, unitData, {
    headers: getHeaders(),
  });
};

/**
 * ChowaUnit更新API
 * @param {number} unitId - ユニットID
 * @param {Object} updateData - 更新データ
 * @param {string} updateData.model_series_id - 機種シリーズID
 * @param {string} updateData.model_type - 機種号機
 * @param {string} updateData.unit_serial_number - ユニット製造番号
 * @param {string} updateData.exhaust_gas - 排ガス規制
 * @param {string} updateData.device - デバイスID
 * @returns {Promise} APIレスポンス
 */
export const updateChowaUnit = (unitId, updateData) => {
  return axios.put(`${CHOWA_UNITS_API}${unitId}/`, updateData, {
    headers: getHeaders(),
  });
};

/**
 * ChowaUnit削除API
 * @param {number} unitId - ユニットID
 * @returns {Promise} APIレスポンス
 */
export const deleteChowaUnit = (unitId) => {
  return axios.delete(`${CHOWA_UNITS_API}${unitId}/delete/`, {
    headers: getHeaders(),
  });
};

// ==============================================
// Chowa Series Management API関数群
// ==============================================

/**
 * シリーズ一覧取得API
 * @returns {Promise} APIレスポンス
 */
export const getChowaSeries = () => {
  return axios.get(CHOWA_SERIES_API, {
    headers: getHeaders(),
  });
};

/**
 * 新規シリーズ作成API
 * @param {Object} seriesData - シリーズデータ {name: string}
 * @returns {Promise} APIレスポンス
 */
export const createChowaSeries = (seriesData) => {
  return axios.post(CHOWA_SERIES_API, seriesData, {
    headers: getHeaders(),
  });
};

/**
 * シリーズ更新API
 * @param {number} seriesId - シリーズID
 * @param {Object} updateData - 更新データ {name: string}
 * @returns {Promise} APIレスポンス
 */
export const updateChowaSeries = (seriesId, updateData) => {
  return axios.put(`${CHOWA_SERIES_API}${seriesId}/`, updateData, {
    headers: getHeaders(),
  });
};

/**
 * シリーズ削除API
 * @param {number} seriesId - シリーズID
 * @returns {Promise} APIレスポンス
 */
export const deleteChowaSeries = (seriesId) => {
  return axios.delete(`${CHOWA_SERIES_API}${seriesId}/`, {
    headers: getHeaders(),
  });
};

// ==============================================
// 機種・排ガス規制取得API関数群
// ==============================================

/**
 * 指定シリーズの機種・排ガス規制の組み合わせを取得
 * @param {string} seriesName - シリーズ名（例: 'SR'）
 * @returns {Promise} APIレスポンス
 */
export const getModelsBySeriesName = (seriesName) => {
  // シリーズキー（例: 'HV'）でフィルタリング
  // バックエンドで model_series__name__icontains で 'HV-300', 'HV-600' などにマッチさせる
  return axios.get(`${CHOWA_UNITS_API}?model_series=${encodeURIComponent(seriesName)}`, {
    headers: getHeaders(),
  });
};

/**
 * 指定シリーズの実際に存在する機種・排ガス規制の組み合わせを取得（重複なし）
 * @param {string} seriesName - シリーズ名（例: 'SR'）
 * @returns {Promise} 機種・排ガス規制の組み合わせ配列（model_type + exhaust_gasのみ）
 */
export const getUniqueModelsBySeriesName = async (seriesName) => {
  try {
    const response = await getModelsBySeriesName(seriesName);
    
    if (response.data?.status === 'success' && response.data?.data?.units) {
      // 機種・排ガス規制の組み合わせを取得（重複なし、個別識別子は除外）
      const uniqueModels = new Set();
      
      response.data.data.units.forEach(unit => {
        if (unit.model_type && unit.exhaust_gas) {
          // model_typeから個別識別子（#1, #2A など）を除去
          // 例: 'HV-300#1' → 'HV-300', 'SR-60#2A' → 'SR-60'
          const baseModelType = unit.model_type.replace(/#.*$/, '');
          
          // 基本機種名 + 排ガス規制の組み合わせ
          const modelName = `${baseModelType}(${unit.exhaust_gas})`;
          uniqueModels.add(modelName);
        }
      });
      
      return Array.from(uniqueModels).sort();
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching unique models:', error);
    return [];
  }
};

// デフォルトエクスポート
const chowaApi = {
  getEngineDataByModelAndExhaust,
  getChowaUnits,
  createChowaUnit,
  updateChowaUnit,
  deleteChowaUnit,
  getChowaSeries,
  createChowaSeries,
  updateChowaSeries,
  deleteChowaSeries,
  getModelsBySeriesName,
  getUniqueModelsBySeriesName,
};

export default chowaApi;
